import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../../lib/api';
import { Card, CardHeader, CardContent } from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import { Save, AlertTriangle, CheckCircle } from 'lucide-react';

const ruleSections = [
  { key: 'daily_income', label: 'Daily Income', fields: [
    { key: 'daily_rate', label: 'Daily Rate (%)', type: 'number' },
    { key: 'max_earning_multiple', label: 'Max Earning Multiple', type: 'number' },
    { key: 'min_investment', label: 'Min Investment', type: 'number' },
    { key: 'max_investment', label: 'Max Investment', type: 'number' },
  ]},
  { key: 'direct_income', label: 'Direct Income', fields: [
    { key: 'direct_commission_pct', label: 'Direct Commission (%)', type: 'number' },
    { key: 'direct_min_investment', label: 'Min Investment for Direct', type: 'number' },
    { key: 'direct_max_commission', label: 'Max Direct Commission', type: 'number' },
  ]},
  { key: 'withdrawal', label: 'Withdrawal Rules', fields: [
    { key: 'min_withdrawal', label: 'Minimum Withdrawal', type: 'number' },
    { key: 'max_withdrawal', label: 'Maximum Withdrawal', type: 'number' },
    { key: 'withdrawal_fee', label: 'Withdrawal Fee (%)', type: 'number' },
    { key: 'withdrawal_kyc_required', label: 'KYC Required', type: 'select', options: ['true', 'false'] },
  ]},
  { key: 'compounding', label: 'Compounding Rules', fields: [
    { key: 'compounding_enabled', label: 'Enabled', type: 'select', options: ['true', 'false'] },
    { key: 'compound_percentage', label: 'Compound Percentage', type: 'number' },
  ]},
  { key: 'security', label: 'Security Rules', fields: [
    { key: 'kyc_required', label: 'KYC Required', type: 'select', options: ['true', 'false'] },
    { key: 'two_fa_required', label: '2FA Required', type: 'select', options: ['true', 'false'] },
    { key: 'max_login_attempts', label: 'Max Login Attempts', type: 'number' },
  ]},
];

export default function AdminBusinessRulesPage() {
  const [activeSection, setActiveSection] = useState('daily_income');
  const [values, setValues] = useState<Record<string, string>>({});
  const [reason, setReason] = useState('');
  const [saveSuccess, setSaveSuccess] = useState(false);
  const queryClient = useQueryClient();

  const { data: settings } = useQuery({
    queryKey: ['adminSettings'],
    queryFn: () => api.get('/settings').then(r => r.data),
  });

  const updateMutation = useMutation({
    mutationFn: (data: { key: string; value: string }[]) => api.patch('/settings/bulk', { updates: data }),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['adminSettings'] }); setSaveSuccess(true); setTimeout(() => setSaveSuccess(false), 3000); },
  });

  const getVal = (key: string) => values[key] ?? settings?.find((s: any) => s.key === key)?.value ?? '';

  const handleSave = () => {
    const section = ruleSections.find(s => s.key === activeSection);
    if (!section) return;
    const updates = section.fields.map(f => ({ key: f.key, value: getVal(f.key) }));
    updateMutation.mutate(updates);
  };

  return (
    <div className="space-y-6 animate-fade-in">
      <div><h1 className="text-2xl font-bold text-white">Business Rules</h1><p className="text-sm text-slate-400">Configure earning, commission, and platform rules</p></div>

      <div className="flex gap-2 p-1 bg-white/[0.04] rounded-lg overflow-x-auto">
        {ruleSections.map(s => (
          <button key={s.key} onClick={() => setActiveSection(s.key)}
            className={"px-4 py-2 text-sm font-medium rounded-md whitespace-nowrap transition-colors " + (activeSection === s.key ? "bg-white/[0.06] text-primary-400 shadow-sm" : "text-slate-300 hover:text-white")}>
            {s.label}
          </button>
        ))}
      </div>

      {saveSuccess && <div className="flex items-center gap-2 p-3 bg-emerald-500/10 border border-emerald-500/20 rounded-lg text-sm text-emerald-400"><CheckCircle className="w-4 h-4" />Rules updated successfully. Changes are logged in audit trail.</div>}

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <h2 className="text-base font-semibold text-white">{ruleSections.find(s => s.key === activeSection)?.label}</h2>
            <Button size="sm" onClick={handleSave} isLoading={updateMutation.isPending}><Save className="w-3.5 h-3.5 mr-1" />Save Changes</Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-4 max-w-xl">
            <div className="flex items-center gap-2 p-3 bg-amber-500/10 border border-amber-500/20 rounded-lg text-xs text-amber-400">
              <AlertTriangle className="w-4 h-4 shrink-0" />
              Changes to business rules are permanent and logged. Previous values are preserved in audit history.
            </div>
            {ruleSections.find(s => s.key === activeSection)?.fields.map(f => (
              <div key={f.key}>
                {f.type === 'select' ? (
                  <div className="space-y-1">
                    <label className="block text-sm font-medium text-slate-200">{f.label}</label>
                    <select value={getVal(f.key)} onChange={e => setValues(v => ({ ...v, [f.key]: e.target.value }))}
                      className="w-full px-3 py-2 border border-white/[0.06] rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white/[0.02] text-white">
                      {f.options?.map(o => <option key={o} value={o}>{o}</option>)}
                    </select>
                  </div>
                ) : (
                  <Input label={f.label} type="number" value={getVal(f.key)} onChange={e => setValues(v => ({ ...v, [f.key]: e.target.value }))} />
                )}
              </div>
            ))}
            <div className="space-y-1">
              <label className="block text-sm font-medium text-slate-200">Reason for Change (required)</label>
              <textarea value={reason} onChange={e => setReason(e.target.value)} rows={2} placeholder="Explain why these rules are being changed..."
                className="w-full px-3 py-2 border border-white/[0.06] rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white/[0.02] text-white placeholder-slate-500" />
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}